import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import PlaceForm from '@web/src/features/place/place-form/place-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreatePlaceInput,
  CreatePlaceMutation,
  CreatePlaceMutationVariables,
  CreatePlaceQuery,
} from 'types/graphql'

const CREATE_PLACE_QUERY: TypedDocumentNode<CreatePlaceQuery> = gql`
  query CreatePlaceQuery {
    placeTags {
      id
      name
    }
  }
`

const CREATE_PLACE_MUTATION: TypedDocumentNode<CreatePlaceMutation, CreatePlaceMutationVariables> = gql`
  mutation CreatePlaceMutation($input: CreatePlaceInput!) {
    createPlace(input: $input) {
      id
      slug
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/place/create')({
  component: CreatePlacePage,
  head: () =>
    seo({
      title: `Create Place | ${env.VITE_APP_NAME}`,
      description: 'Create a new Place entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, CREATE_PLACE_QUERY)
    return { queryRef }
  },
})

function CreatePlacePage() {
  const { t } = useTranslation()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<CreatePlaceQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [createPlace] = useMutation(CREATE_PLACE_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Place created'))
      navigate({
        to: '/place/$placeIdOrSlug',
        params: {
          placeIdOrSlug: result.createPlace.slug ?? result.createPlace.id,
        } as never,
      })
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreatePlaceInput) => {
    setShowSpinnerValue(true)
    createPlace({ variables: { input } })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.placeTags) {
    return <ErrorAlert error={new Error(t('Place tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Create Place')}</h1>
        <PlaceForm onSave={onSave} placeTags={data.placeTags} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
